import { CdkConnectedOverlay, ConnectedPosition, ConnectionPositionPair } from '@angular/cdk/overlay';
import { Component, computed, effect, EventEmitter, inject, Input, input, OnDestroy, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ObjectTypes } from '@common/enums/object-types';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { DraggableEnpsComponent } from '@src/suite/common/components/base/draggable-enps-component';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { FieldDefValidations } from '@src/suite/common/enums/enums';
import { HighlightPipe } from '@src/suite/common/pipes/highlight';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { selectCastViewStateCustomCardItems } from '@src/suite/common/store/cast/cast.selectors';
import { selectClientTimeZone, selectPersonalTimeZone } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectViewStateSearchString } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { getItemString } from '@src/suite/common/utils/util';

@Component({
    selector: 'st-cast-object-item',
    templateUrl: './cast-object-item.html',
    styleUrls: ['./cast-object-item.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-cast-object',
    },
})
export class CastObjectItemComponent extends DraggableEnpsComponent implements OnDestroy {
    ObjectTypesEnum = ObjectTypes;
    isPanelOpen: boolean = false;
    PSTFieldNames = PSTFieldNames;
    readonly FieldDefValidations = FieldDefValidations;
    public readonly getItemString = getItemString;

    globalTablesService = inject(GlobalTablesService);
    appService = inject(AppService);
    store = inject(Store);
    visualStateService = inject(VisualStateService);
    highlightPipe = inject(HighlightPipe);

    @Input() isLast: boolean = false;

    customCardItems = input<FieldDef[]>([]);
    public itemStringCache = new Map<string, string>();

    data = input<StorytellingAssignmentRecord | StorytellingTopicRecord>();

    languageSettings = this.store.selectSignal(selectI18N);
    i18n = this.store.selectSignal(selectI18N);
    searchString = this.store.selectSignal(selectViewStateSearchString);
    personalTimeZone = this.store.selectSignal(selectPersonalTimeZone);
    clientTimeZone = this.store.selectSignal(selectClientTimeZone);
    viewStateCustomItems = this.store.selectSignal(selectCastViewStateCustomCardItems);

    _disabled: boolean;

    @Input() get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: boolean) {
        if (this.data().PSTApproved) {
            value = this.data().PSTApproved;
        }
        this._disabled = value;
    }

    @ViewChild(CdkConnectedOverlay) overlay: CdkConnectedOverlay;
    @Output() itemClick = new EventEmitter<StorytellingTopicRecord>();

    constructor() {
        super();
        effect(() => {
            if (this.data()) {
                this.itemStringCache.clear();
            }
        });
        // effect(() => {
        //     // this._guid = `candidate|${this.data()?.Guid}`;
        // });
    }

    ownersLabel = computed(() => {
        if (this.data().Type === ObjectTypes.TOPIC) {
            return this.globalTablesService.FieldDefsDict[PSTFieldNames?.PSTTopicStoryOwner]?.DefaultCaption;
        }
        if (this.data().Type === ObjectTypes.SCRIPT) {
            return this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTStoryOwner]?.DefaultCaption;
        }
        return this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentOwner]?.DefaultCaption;
    });

    owners = computed(() => {
        if (this.data().Type === ObjectTypes.TOPIC) {
            return (this.data() as StorytellingTopicRecord).PSTTopicOwner;
        }
        if (this.data()?.Type === ObjectTypes.SCRIPT) {
            return (this.data() as StorytellingStoryRecord)?.PSTStoryOwner;
        }
        return (this.data() as StorytellingAssignmentRecord)?.PSTAssignmentOwner;
    });

    status = computed(() => {
        if (this.data().Type === ObjectTypes.TOPIC) {
            return (this.data() as StorytellingTopicRecord).PSTTopicStatus;
        }
        if (this.data()?.Type === ObjectTypes.SCRIPT) {
            return (this.data() as StorytellingStoryRecord)?.PSTStoryStatus;
        }
        return (this.data() as StorytellingAssignmentRecord)?.PSTAssignmentStatus;
    });

    getInnerProp(item: FieldDef) {
        const val = this.data()?.[item.ID.split('-')[0]];
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }

    currentTimeZone = computed(() => {
        return this.personalTimeZone() ? this.personalTimeZone() : this.clientTimeZone();
    });

    assigmentType = computed(() => {
        return (this.data() as StorytellingAssignmentRecord)?.PSTCoverageType;
    });

    desk = computed(() => {
        return this.data().PSTGroup;
    });
    approvedLabel = computed(() => {
        if (this.data()?.Type === ObjectTypes.TOPIC || this.data()?.Type === ObjectTypes.ASSIGNMENT || this.data()?.Type === ObjectTypes.SCRIPT) {
            return this.data().PSTApproved ? 'Approved' : 'Approve';
        }
    });

    assignment = computed(() => {
        if (this.data()?.Type === ObjectTypes.ASSIGNMENT) {
            return this.data();
        }
        return null;
    });

    assignmentIconType = computed(() => {
        if (this.data()?.Type === ObjectTypes.ASSIGNMENT) {
            return (this.data() as StorytellingAssignmentRecord)?.PSTCoverageType;
        }
        return null;
    });

    getItemCreator = computed(() => {
        return `${this.languageSettings().PSTWebCastObjectsCreatedBy} ${this.data().Creator}`;
    });

    get connectedPositions(): ConnectedPosition[] {
        return [
            new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'end', overlayY: 'top' }),
            new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'end', overlayY: 'bottom' }),
        ];
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.isPanelOpen = false;
    }

    public onMouseMove(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-cast-object-item-container-overlay')) {
            this.isPanelOpen = false;
        }
    }

    panelAttached() {
        const overlayElement = this.overlay?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.isPanelOpen = false;
            });
        }
    }

    updateTopic(value: any, fieldId: string) {
        const fieldDef = [{ value, key: fieldId }];
        // TODO: not cool to have this title logic here
        if (fieldId === PSTFieldNames.Title) {
            this.data().Title = value.trim();
            this.data().Slug = value.trim();
            fieldDef.push({ value: value, key: PSTFieldNames.Slug });
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }
}
